import { useEffect, useRef, useState } from "react";
import { X, Upload, Loader2, FileText, CheckCircle } from "lucide-react";
import { upload, MIME_ACEITOS, MIME_LIMITS } from "../../services/midiaApi";

const TIPOS = [
  { id: "image",    label: "Imagem",    accept: MIME_ACEITOS.image    },
  { id: "video",    label: "Vídeo",     accept: MIME_ACEITOS.video    },
  { id: "audio",    label: "Áudio",     accept: MIME_ACEITOS.audio    },
  { id: "document", label: "Documento", accept: MIME_ACEITOS.document },
];

/**
 * Modal de upload de mídia para mensagens WhatsApp.
 * Valida tamanho client-side, exibe progresso do upload e chama onUploaded
 * com o media_id retornado pelo backend após upload bem-sucedido.
 *
 * @param {{
 *   contaId: number,
 *   onUploaded: (mediaId: string, mimeType: string, fileSize: number) => void,
 *   onClose: () => void,
 * }} props
 */
export default function MediaUploader({ contaId, onUploaded, onClose }) {
  const [tipo,      setTipo]      = useState("image");
  const [arquivo,   setArquivo]   = useState(null);
  const [preview,   setPreview]   = useState(null);
  const [progresso, setProgresso] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [concluido, setConcluido] = useState(false);
  const [erro,      setErro]      = useState(null);
  const inputRef = useRef(null);
  const modalRef = useRef(null);

  useEffect(() => { modalRef.current?.focus(); }, []);

  useEffect(() => {
    function handleEsc(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  // Revoga blob URL ao desmontar para evitar leak de memória
  useEffect(() => {
    return () => {
      if (preview?.startsWith("blob:")) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  function handleTipoChange(novoTipo) {
    setTipo(novoTipo);
    setArquivo(null);
    if (preview?.startsWith("blob:")) URL.revokeObjectURL(preview);
    setPreview(null);
    setErro(null);
    setProgresso(0);
    setConcluido(false);
  }

  function handleArquivoSelecionado(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    const limite = MIME_LIMITS[tipo];
    if (limite && file.size > limite) {
      const mb = Math.round(limite / 1024 / 1024);
      setErro(`Arquivo muito grande. Limite para ${tipo}: ${mb} MB.`);
      return;
    }

    setArquivo(file);
    setErro(null);
    setProgresso(0);
    setConcluido(false);

    if (tipo === "image") {
      if (preview?.startsWith("blob:")) URL.revokeObjectURL(preview);
      setPreview(URL.createObjectURL(file));
    } else {
      setPreview(null);
    }
  }

  async function handleUpload() {
    if (!arquivo || uploading || concluido) return;

    setUploading(true);
    setErro(null);
    setProgresso(0);

    try {
      const resultado = await upload(contaId, tipo, arquivo, setProgresso);
      setConcluido(true);
      setProgresso(100);
      onUploaded(resultado.media_id, resultado.mime_type, resultado.file_size);
      onClose();
    } catch (err) {
      setErro(err?.message ?? err?.response?.data?.error ?? "Erro ao fazer upload.");
    } finally {
      setUploading(false);
    }
  }

  const tipoAtual  = TIPOS.find((t) => t.id === tipo);
  const limiteMb   = MIME_LIMITS[tipo] ? Math.round(MIME_LIMITS[tipo] / 1024 / 1024) : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      role="dialog"
      aria-modal="true"
      aria-label="Enviar arquivo de mídia"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={modalRef}
        tabIndex={-1}
        className="bg-surface-1 border border-white/[0.08] rounded-xl shadow-2xl w-full max-w-sm flex flex-col outline-none"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06] flex-shrink-0">
          <h2 className="text-sm font-semibold text-ink-1 flex items-center gap-2">
            <Upload className="w-4 h-4 text-brand-400" aria-hidden="true" />
            Enviar mídia
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar modal"
            className="text-ink-4 hover:text-ink-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded p-0.5"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 flex flex-col gap-4">
          {/* Seletor de tipo */}
          <div>
            <p className="text-xs text-ink-3 mb-2">Tipo de mídia</p>
            <div className="grid grid-cols-4 gap-1.5" role="group" aria-label="Selecionar tipo de mídia">
              {TIPOS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => handleTipoChange(t.id)}
                  aria-pressed={tipo === t.id}
                  className={`py-1.5 rounded text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
                    tipo === t.id
                      ? "bg-brand-600 text-white"
                      : "bg-surface-2 text-ink-3 hover:text-ink-2 hover:bg-surface-3"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            {limiteMb && (
              <p className="text-xs text-ink-5 mt-1.5">Limite: {limiteMb} MB</p>
            )}
          </div>

          {/* Área de seleção de arquivo */}
          <div>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              aria-label={`Selecionar arquivo de ${tipoAtual?.label ?? tipo}`}
              className="w-full border-2 border-dashed border-white/[0.08] hover:border-brand-600 rounded-lg py-6 flex flex-col items-center gap-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              {arquivo ? (
                <>
                  <FileText className="w-6 h-6 text-brand-400" aria-hidden="true" />
                  <span className="text-xs text-ink-2 font-medium px-4 text-center break-all">
                    {arquivo.name}
                  </span>
                  <span className="text-xs text-ink-4">
                    {(arquivo.size / 1024 / 1024).toFixed(2)} MB
                  </span>
                </>
              ) : (
                <>
                  <Upload className="w-6 h-6 text-ink-5" aria-hidden="true" />
                  <span className="text-xs text-ink-4">
                    Clique para selecionar {tipoAtual?.label ?? tipo}
                  </span>
                </>
              )}
            </button>
            <input
              ref={inputRef}
              type="file"
              accept={tipoAtual?.accept ?? "*/*"}
              onChange={handleArquivoSelecionado}
              aria-label="Campo de upload de arquivo"
              className="sr-only"
            />
          </div>

          {/* Preview imagem */}
          {preview && tipo === "image" && (
            <img
              src={preview}
              alt="Preview da imagem selecionada"
              className="w-full max-h-40 object-contain rounded-lg border border-white/[0.08] bg-surface-2"
            />
          )}

          {/* Barra de progresso */}
          {uploading && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-ink-3">Enviando...</span>
                <span className="text-xs text-ink-3">{progresso}%</span>
              </div>
              <div className="h-1.5 bg-surface-2 rounded-full overflow-hidden">
                <div
                  className="h-full bg-brand-600 transition-all duration-200 rounded-full"
                  style={{ width: `${progresso}%` }}
                  role="progressbar"
                  aria-valuenow={progresso}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={`Progresso do upload: ${progresso}%`}
                />
              </div>
            </div>
          )}

          {concluido && (
            <div className="flex items-center gap-2 text-emerald-400" role="status">
              <CheckCircle className="w-4 h-4" aria-hidden="true" />
              <span className="text-xs font-medium">Upload concluído</span>
            </div>
          )}

          {erro && (
            <p className="text-xs text-red-400" role="alert">{erro}</p>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-white/[0.06] flex items-center justify-end gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-ink-3 hover:text-ink-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded px-3 py-1.5"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleUpload}
            disabled={!arquivo || uploading || concluido}
            aria-label="Fazer upload e enviar"
            className="flex items-center gap-1.5 text-xs font-medium bg-brand-600 hover:bg-brand-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg px-4 py-1.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
          >
            {uploading
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
              : <Upload className="w-3.5 h-3.5" aria-hidden="true" />}
            {uploading ? "Enviando..." : "Enviar"}
          </button>
        </div>
      </div>
    </div>
  );
}
